import { ImageCapturePageNumber, LandingPageNumber, ExteriorPageNumber, InteriorPageNumber, MechanicalPageNumber, ContactPageNumber, LoadingPageNumber, CertificatePageNumber, VehiclePageNumber } from '../../constants'
import Events from './asc/eventType'
import { COMM_OUTCOME, CRM_UPDATE, ELEMENT_SUB_TYPE, ELEMENT_TEXT, ELEMENT_TITLE, EVENT_ACTION, EVENT_ACTION_RESULT, FLOW_OUTCOME } from './asc/parameter'
import { addScript } from '../util'
import { StringUtils } from '../../utils/siteUtils'

const getTagId = dataLayer => dataLayer && dataLayer.measurementId

const ENTRY = {
  vin: 'vin',
  lp: 'license_plate',
  ymm: 'make_model',
  mileage: 'mileage'
}

const initGtag = dataLayer => {
  const tagId = getTagId(dataLayer)
  if (tagId) {
    const scriptAdded = addScript(`www.googletagmanager.com/gtag/js?id=${tagId}`)
    if (scriptAdded) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push('js', new Date())
      window.dataLayer.push('config', tagId)
    }
  }
}

function gtag() {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(arguments)
}

const sendGTag = (dataLayer, event) => {
  const tagId = getTagId(dataLayer)
  if (tagId && event) {
    gtag('event', event.event_name, { send_to: tagId, ...event })
  }
}

const sendAscPageViewEvent = dataLayer => {
  sendGTag(dataLayer, Events.ASC_PAGE_VIEW)
}

const sendAscEvent = (dataLayer, landingContext) => {
  const events = getAscEvents(dataLayer, landingContext) || []
  events.forEach(event => {
    sendGTag(dataLayer, event)
  })
}

export const getAscEvents = (dataLayer, landingContext) => {
  const { pageInfo, vehicle } = dataLayer
  const { page: pageNumber } = pageInfo
  const linkName = pageInfo?.linkName || ''
  const events = []
  const isComponentsEnabled = landingContext?.usingLandingWebComponent
  const isOfferCreationComponentEnabled = landingContext?.globalState?.isOfferCreationComponentEnabled

  const isBack = linkName === 'back'
  const isNext = ['continue', 'continue_to_contact'].includes(linkName)
  const isSubmitting = pageNumber === ContactPageNumber
  if (isBack || (isNext && !isSubmitting)) {
    events.push(...getNextBackEvents(linkName, pageNumber))
  }

  // B3, C2 only error case
  if (linkName === 'continue_error' || linkName === 'image_capture_continue_error') {
    const elementSubtype = linkName === 'image_capture_continue_error' ? ELEMENT_SUB_TYPE.button : undefined
    events.push(
      {
        ...Events.ASC_CTA_INTERACTION,
        element_text: ELEMENT_TEXT.next,
        element_subtype: ELEMENT_SUB_TYPE.button,
        event_action: EVENT_ACTION.click,
        element_subtype: elementSubtype,
        event_action_result: EVENT_ACTION_RESULT.error
      },
      {
        ...Events.ASC_RETAIL_PROCESS,
        element_title: ELEMENT_TITLE[pageInfo.page],
        flow_outcome: EVENT_ACTION_RESULT.declined
      }
    )
  }

  switch (pageNumber) {
    case LandingPageNumber:
      const goButtonClicked = !isComponentsEnabled && (linkName === 'continue_timbuybkhome_selltrade_vin' || linkName === 'continue_timbuybkhome_selltrade_lp')
      const goButtonLPError = !isComponentsEnabled && !linkName.includes('pixall') && linkName.includes('continue_timbuybkhome_selltrade') && (linkName.includes('error') || linkName.includes('invalid') || linkName.includes('ineligible'))
      const startMyOfferClicked = isComponentsEnabled && linkName.startsWith('cta::start-my-offer-success')
      const goButtonClickedNoError = goButtonClicked && !linkName.includes('error') && !linkName.includes('invalid') && !linkName.includes('ineligible')
      const getStartedButtonClicked = ['styleselect', 'mileageselect'].some(text => linkName.includes(text)) || ['cta::style::next'].includes(linkName) || ['cta::mileage::next'].includes(linkName)
      const continueButtonClicked = linkName.includes('_mm')

      if (startMyOfferClicked || goButtonClicked || continueButtonClicked) {
        // A1 | 'Continue' & 'Go' button
        const actionResult = linkName.includes('ineligible') || linkName.includes('error') ? EVENT_ACTION_RESULT.error : EVENT_ACTION_RESULT.start
        const elementText = continueButtonClicked ? ELEMENT_TEXT.continue : startMyOfferClicked ? ELEMENT_TEXT.startMyOffer : ELEMENT_TEXT.go
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: elementText,
          event_action: EVENT_ACTION.click,
          event_action_result: actionResult,
          element_subtype: ELEMENT_SUB_TYPE.button
        })
      } else if (goButtonLPError || linkName.startsWith('cta::start-my-offer-error') || linkName.startsWith('cta::start-my-offer-ineligible')) {
        // A1 on Error Case
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: ELEMENT_TEXT.startMyOffer,
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.error,
          element_subtype: ELEMENT_SUB_TYPE.button
        })
      }
      if (getStartedButtonClicked) {
        // A1 | 'Get Started' button
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: ['cta::mileage::next', 'cta::style::next'].some(text => linkName.includes(text)) ? ELEMENT_TEXT.next : ELEMENT_TEXT.getStarted,
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.complete,
          element_subtype: ELEMENT_SUB_TYPE.button
        })
      }

      if (startMyOfferClicked || goButtonClickedNoError || continueButtonClicked || getStartedButtonClicked) {
        // A2
        events.push({
          ...Events.ASC_RETAIL_PROCESS,
          element_title: getStartedButtonClicked ? ENTRY['mileage'] : ENTRY[vehicle.entry],
          flow_outcome: getStartedButtonClicked ? FLOW_OUTCOME.proceed : FLOW_OUTCOME.start
        })
      }

      if (linkName.startsWith('whereismyvin_lnk') || linkName === 'text-link::whereismyvin') {
        // A3
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: 'Where is my VIN?',
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.popup,
          element_subtype: ELEMENT_SUB_TYPE.hyperlink
        })
      }

      break

    case VehiclePageNumber:
      if (linkName === 'cta::standard_equipment' || linkName === 'text-link::see-standard-equipment') {
        events.push({
          // B2
          ...Events.ASC_CTA_INTERACTION,
          element_text: ELEMENT_TEXT.seeStandardEquipment,
          event_action: EVENT_ACTION.click,
          element_subtype: ELEMENT_SUB_TYPE.hyperlink,
          event_action_result: EVENT_ACTION_RESULT.popup
        })
      } else if (linkName === 'text-link::see-all-features-equipment') {
        events.push({
          // B5
          ...Events.ASC_CTA_INTERACTION,
          element_text: ELEMENT_TEXT.seeAllFeaturesEquipment,
          event_action: EVENT_ACTION.click,
          element_subtype: ELEMENT_SUB_TYPE.hyperlink,
          event_action_result: EVENT_ACTION_RESULT.popup
        })
      } else if (linkName.startsWith('ico_mods')) {
        // B4
        let title = linkName.replace('ico_mods_', '')
        title = title === 'wheels-tires' ? 'wheelsTires' : title
        const element_text = ELEMENT_TEXT[title]
        if (element_text) {
          events.push({
            ...Events.ASC_CTA_INTERACTION,
            element_text: ELEMENT_TEXT[title],
            event_action: EVENT_ACTION.click,
            element_subtype: ELEMENT_SUB_TYPE.icon,
            event_action_result: EVENT_ACTION_RESULT.popup
          })
        }
      }
      break
    case ImageCapturePageNumber:
      if (linkName === 'text-link::see-photo-guidelines') {
        // D2
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: 'See photo guidelines',
          element_subtype: ELEMENT_SUB_TYPE.hyperlink,
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.popup
        })
      }
      if (linkName === 'cta::add_photo') {
        // D3
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: 'Add photo',
          element_subtype: ELEMENT_SUB_TYPE.hyperlink,
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.popup
        })
      }
      break

    case ExteriorPageNumber:
    case InteriorPageNumber:
    case MechanicalPageNumber:
      switch (linkName) {
        case 'continue_modal':
          // F1 F2 only Next button
          events.push({
            ...Events.ASC_CTA_INTERACTION,
            element_text: ELEMENT_TEXT.next,
            event_action: EVENT_ACTION.click,
            element_subtype: ELEMENT_SUB_TYPE.button,
            event_action_result: EVENT_ACTION_RESULT.popup
          })
          break

        case 'cta::modal::return':
        case 'sm_return_to_questionnaire_btn':
          // F5
          events.push({
            ...Events.ASC_CTA_INTERACTION,
            element_text: 'Return to Questionnaire',
            event_action: EVENT_ACTION.click,
            element_subtype: ELEMENT_SUB_TYPE.button,
            event_action_result: EVENT_ACTION_RESULT.close
          })
          break

        case 'sm_skip_to_next_steps_modal_btn':
          // F3 F4
          fireAscYesItsPerfect(events)
          break

        default:
          break
      }

      break

    case ContactPageNumber:
      if (linkName === 'cta::modal::skip') {
        fireAscYesItsPerfect(events)
      }

      const errorOfferFury = !isOfferCreationComponentEnabled && StringUtils.startsWithAny(linkName, ['continue_error', 'contact_error'])
      const errorOfferComponent = linkName === 'asc_contact_error'
      if (errorOfferFury || errorOfferComponent) {
        events.push(
          {
            // G1: error
            ...Events.ASC_RETAIL_PROCESS,
            element_title: ELEMENT_TITLE[ContactPageNumber],
            flow_outcome: FLOW_OUTCOME.error
          },
          {
            // G2: error
            ...Events.ASC_CTA_INTERACTION,
            element_text: ELEMENT_TEXT.getMyOffer,
            element_subtype: ELEMENT_SUB_TYPE.button,
            event_action: EVENT_ACTION.click,
            event_action_result: EVENT_ACTION_RESULT.error
          }
        )
      }

      if (StringUtils.includesAny(linkName, ['contact_input', 'voi_'])) {
        const input = linkName.split('/')[0]
        if (engagedContactInputs.includes(input)) return
        // G3 - Fury
        const comm_status = engagedContactInputs.length === 0 ? 'start' : 'engage'
        engagedContactInputs.push(input)
        const element_text = input.split(':')[1]

        events.push({
          ...Events.ASC_FORM_ENGAGEMENT,
          comm_status,
          element_text
        })
      }

      if (linkName.startsWith('form_engagement')) {
        // G3 - Offer Creation Component
        const [, comm_status, element_text] = linkName.split('::')
        events.push({
          ...Events.ASC_FORM_ENGAGEMENT,
          comm_status,
          element_text
        })
      }

      break

    case LoadingPageNumber:
      if (linkName === 'summary_submit_offer') {
        events.push({
          // G3
          ...Events.ASC_FORM_ENGAGEMENT,
          comm_status: CRM_UPDATE,
          element_text: ELEMENT_TEXT.getMyOffer
        })
      } else if (linkName === 'submit_offer::success') {
        const common = {
          ...COMM_OUTCOME.crm_update,
          element_text: ELEMENT_TEXT.getMyOffer,
          form_name: 'ico_contact'
        }
        events.push(
          {
            // G1: submit
            ...Events.ASC_RETAIL_PROCESS,
            element_title: ELEMENT_TITLE[ContactPageNumber],
            flow_outcome: FLOW_OUTCOME.submit
          },
          {
            // G2
            ...Events.ASC_CTA_INTERACTION,
            element_text: ELEMENT_TEXT.getMyOffer,
            element_subtype: ELEMENT_SUB_TYPE.button,
            event_action: EVENT_ACTION.click,
            event_action_result: EVENT_ACTION_RESULT.complete
          },
          {
            // G4
            ...Events.ASC_FORM_SUBMISSION,
            ...common
          },
          {
            // G5
            ...Events.ASC_FORM_SUBMISSION_SALES,
            ...common
          }
        )
      }
      break

    case CertificatePageNumber:
      if (['cta::certificate_header::', 'text-link::'].some(text => linkName.includes(text))) {
        // H2, H3, H8, H9, H10
        let textLink = linkName.split('::').at(-1)
        const isReviewDetails = textLink === 'review_vehicle_details'
        textLink = isReviewDetails ? 'reviewVehicleDetails' : textLink
        const elementText = ELEMENT_TEXT[textLink]
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: elementText || textLink,
          event_action: EVENT_ACTION.click,
          element_subtype: elementText ? ELEMENT_SUB_TYPE.hyperlink : ELEMENT_SUB_TYPE.button,
          event_action_result: isReviewDetails ? EVENT_ACTION_RESULT.popup : EVENT_ACTION_RESULT.open
        })
      }
      if (linkName.includes('vehicle-history-report')) {
        // H2
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: 'Get full report',
          element_subtype: ELEMENT_SUB_TYPE.hyperlink,
          event_action: EVENT_ACTION.click,
          event_action_result: EVENT_ACTION_RESULT.open
        })
      }
      if (linkName.startsWith('button::request-appointment::')) {
        // H4 + H11
        const isDealerRA = linkName.endsWith('dealer')
        events.push({
          ...Events.ASC_CTA_INTERACTION,
          element_text: ELEMENT_TEXT.requestAppointment,
          event_action: EVENT_ACTION.click,
          element_subtype: ELEMENT_SUB_TYPE.button,
          event_action_result: isDealerRA ? EVENT_ACTION_RESULT.popup : EVENT_ACTION_RESULT.none
        })
        if (isDealerRA) {
          events.push({
            // H12
            ...Events.ASC_RETAIL_PROCESS,
            element_title: 'request_appointment',
            flow_outcome: FLOW_OUTCOME.proceed
          })
        }
      }
      if (linkName === 'submit::ico::request-appointment') {
        events.push(
          {
            // H5
            ...Events.ASC_CTA_INTERACTION,
            element_text: ELEMENT_TEXT.requestAppointment,
            event_action: EVENT_ACTION.click,
            element_subtype: ELEMENT_SUB_TYPE.button,
            event_action_result: EVENT_ACTION_RESULT.complete
          },
          {
            // H6
            ...Events.ASC_RETAIL_PROCESS,
            element_title: 'request_appointment',
            flow_outcome: FLOW_OUTCOME.submit
          },
          {
            // H7
            ...Events.ASC_FORM_SUBMISSION_SALES_APPT,
            element_text: ELEMENT_TEXT.requestAppointment,
            comm_type: 'form',
            comm_outcome: 'appt_booked',
            form_name: 'ico_offer'
          }
        )
      }

      if (linkName === 'cta::certificate_header::call' || linkName === 'text-link::phone') {
        // H13
        const { certificateInfo } = dataLayer
        const { dealer } = certificateInfo || {}
        const phoneNumber = dealer?.dealerPhone || '1-866-559-5268'
        events.push({
          // H13
          ...Events.ASC_CLICK_TO_CALL,
          comm_phone_number: phoneNumber
        })
      }

      if (linkName.startsWith('accept-or-counteroffer')) {
        const CTA_BUTTON = {
          ...Events.ASC_CTA_INTERACTION,
          element_subtype: ELEMENT_SUB_TYPE.button,
          event_action_result: EVENT_ACTION_RESULT.popup
        }
        const ACCEPT_OFFER = 'accept_offer'
        const COUNTEROFFER = 'counteroffer'

        const action = linkName.replace('accept-or-counteroffer::', '')
        switch (action) {
          case 'cta::modal::accept-offer': // A1
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Accept Offer'
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: ACCEPT_OFFER,
                flow_outcome: FLOW_OUTCOME.proceed
              }
            )
            break

          case 'cta::accept-offer::yes': // A2
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Yes!',
                event_action_result: EVENT_ACTION_RESULT.complete
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: ACCEPT_OFFER,
                flow_outcome: FLOW_OUTCOME.submit
              }
            )
            break

          case 'cta::modal::make-a-counteroffer': // B1
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Make a Counteroffer'
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: 'Make a Counteroffer',
                flow_outcome: FLOW_OUTCOME.proceed
              }
            )
            break

          case 'cta::counteroffer::make': // B2
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Make a Counteroffer'
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: COUNTEROFFER,
                flow_outcome: FLOW_OUTCOME.proceed
              }
            )
            break

          case 'cta::counteroffer::edit': // B4
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Edit Counteroffer',
                event_action_result: EVENT_ACTION_RESULT.restart
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: COUNTEROFFER,
                flow_outcome: FLOW_OUTCOME.back
              }
            )
            break

          case 'cta::counteroffer::submit': // B5
            events.push(
              {
                ...CTA_BUTTON,
                element_text: 'Submit Counteroffer',
                event_action_result: EVENT_ACTION_RESULT.complete
              },
              {
                ...Events.ASC_RETAIL_PROCESS,
                element_title: COUNTEROFFER,
                flow_outcome: FLOW_OUTCOME.submit
              }
            )
            break

          default:
            const CLOSE_BUTTON = {
              ...Events.ASC_RETAIL_PROCESS,
              flow_outcome: action.endsWith('finished') ? FLOW_OUTCOME.finished : FLOW_OUTCOME.close
            }
            if (action.includes('button::accept-offer::close'))
              // A3 + A4
              events.push({
                ...CLOSE_BUTTON,
                element_title: ACCEPT_OFFER
              })
            else if (action.includes('button::counteroffer::close'))
              // B3 + B6 + B7
              events.push({
                ...CLOSE_BUTTON,
                element_title: COUNTEROFFER
              })
            break
        }
      }
      break

    default:
      break
  }

  return events
}

const fireAscYesItsPerfect = events => {
  events.push(
    {
      ...Events.ASC_RETAIL_PROCESS,
      element_title: ELEMENT_TITLE[ExteriorPageNumber],
      flow_outcome: FLOW_OUTCOME.proceed
    },
    {
      ...Events.ASC_CTA_INTERACTION,
      element_text: ELEMENT_TEXT.yesItIsPerfect,
      event_action: EVENT_ACTION.click,
      element_subtype: ELEMENT_SUB_TYPE.button,
      event_action_result: EVENT_ACTION_RESULT.complete
    }
  )
}

const getNextBackEvents = (linkName, pageNumber) => {
  const isNextButton = linkName.includes('continue')
  return [
    {
      ...Events.ASC_RETAIL_PROCESS,
      element_title: ELEMENT_TITLE[pageNumber],
      flow_outcome: isNextButton ? FLOW_OUTCOME.proceed : FLOW_OUTCOME.back
    },
    {
      ...Events.ASC_CTA_INTERACTION,
      element_text: isNextButton ? ELEMENT_TEXT.next : ELEMENT_TEXT.back,
      element_subtype: ELEMENT_SUB_TYPE.button,
      event_action: EVENT_ACTION.click,
      event_action_result: isNextButton ? EVENT_ACTION_RESULT.complete : EVENT_ACTION_RESULT.exit
    }
  ]
}

const engagedContactInputs = []

const asc_continue = (dataLayer, landingContext) => {
  try {
    initGtag(dataLayer)
    sendAscEvent(dataLayer, landingContext)
  } catch (e) {
    console.log('ASC continue error', e)
  }
}

const asc_pageReady = (dataLayer, landingContext) => {
  try {
    var intervalId = setInterval(() => {
      if (getTagId(dataLayer)) {
        initGtag(dataLayer)
        sendAscPageViewEvent(dataLayer)
        clearInterval(intervalId)
      }
    }, 100)
  } catch (e) {
    console.log('ASC page ready error', e)
  }
}

export { asc_continue, asc_pageReady }
